import CameraNotifications from '@components/camera-notification';
import AddGeoZonesCameraPTZ from '@pages/cameras/details/components/tabsGeozonesDetail/ptz/upsertGeoZones/components/AddGeoZonesPTZ';
import ReportBI from '@pages/report';
import InsightHeatMap from '@pages/report/InsightHeatMap';
import { VerificationProvider, VerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import { lazy } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';
import './App.css';
import AuthLayout from './layout/AuthLayout';

const EcamV2TracksPage = lazy(() => import('@pages/ecam-v2/tracks'));
const EcamV2DashboardPage = lazy(() => import('@pages/ecam-v2/dashboard'));
const EcamV2ReviewZonesPage = lazy(() => import('@pages/ecam-v2/review'));
const EcamV2ReviewDetailPage = lazy(() => import('@pages/ecam-v2/review-detail'));
const EcamV2TracksDashboardPage = lazy(() => import('@pages/ecam-v2/tracks-dashboard'));

const HomeScreen = lazy(() => import('@pages/HomeScreen'));
const CameraUpsert = lazy(() => import('@pages/cameras/add-new'));
const DetailsCamera = lazy(() => import('@pages/cameras/details'));
const Cameras = lazy(() => import('@pages/cameras/list'));
const Locations = lazy(() => import('@pages/locations'));
const MapOverview = lazy(() => import('@pages/map-overview/index'));
const ZoneDashboard = lazy(() => import('@pages/zone-dashboard/index'));
const LivePhotosPage = lazy(() => import('@pages/cameras/live-photos'));
const Logout = lazy(() => import('@pages/logout'));
const VerificationsOverviewScreen = lazy(() => import('@pages/overview/VerificationsOverviewScreen'));
const ListPCByZone = lazy(() => import('@pages/verification/list-pc-by-zone'));
const WrapPCDetail = lazy(() => import('@pages/verification/pc-details'));
const VerificationsByZone = lazy(() => import('@pages/verification/zone-detail'));
const AddGeoZones = lazy(() => import('@pages/cameras/details/components/AddGeoZones'));
const EditGeoZones = lazy(() => import('@pages/cameras/details/components/EditGeoZones'));
const ReportsAnalyzing = lazy(() => import('@pages/overview'));
const CameraOverViewPage = lazy(() => import('@pages/camerasOverview/pages'));
const CameraOverviewPageContext = lazy(() => import('@pages/camerasOverview'));
const PageNotFound = lazy(() => import('@components/page-not-found/index'));
const TaskManagementScreen = lazy(() => import('@pages/task-management'));
const PCNCreationScreen = lazy(() => import('@pages/manual-create-pcn'));
const UpsertGeoZonesHandler = lazy(() => import('@pages/cameras/hook/GeoZonesNavigateHandler'));
const GeoZonesDetailCameraPTZ = lazy(
    () => import('@pages/cameras/details/components/tabsGeozonesDetail/ptz/detailGeoZones')
);
const PermitsEcamVehicleScreen = lazy(() => import('@pages/permits-vehicle-eCam'));
const ExemptionCreateScreen = lazy(() => import('@pages/exemptions/create'));
const ConfigurationTemplateScreen = lazy(() => import('@pages/configuration-template'));
const CarParkingListScreen = lazy(() => import('@pages/car-parking-list'));
const CarParkingDetails = lazy(() => import('@pages/car-parking-list/components/details'));
const ZoneManagement = lazy(() => import('@pages/zone-management/index'));
const ZoneDetails = lazy(() => import('@pages/zone-management/zone-details/index'));
const SIMManagement = lazy(() => import('@pages/sim-management'));
const CheckPhotoScreen = lazy(() => import('@pages/check-photo'));
const BackdateDetailScreen = lazy(
    () => import('@pages/cameras/details/components/tab-backdate-pcn/components/BackdateDetailScreen')
);
const BackdateReviewScreen = lazy(
    () => import('@pages/cameras/details/components/tab-backdate-pcn/components/BackdateReviewScreen')
);
const NewBackdateScreen = lazy(
    () => import('@pages/cameras/details/components/tab-backdate-pcn/components/NewBackdateScreen')
);

const router = createBrowserRouter([
    {
        path: '/',
        element: <AuthLayout />,

        children: [
            {
                index: true,
                element: <Navigate to={'cameras'} />,
            },
            {
                path: 'home',
                element: <HomeScreen />,
            },
            {
                path: 'cameras',
                element: <Cameras />,
            },
            {
                path: 'cameras/add',
                element: <CameraUpsert />,
            },
            {
                path: 'cameras/add/:type',
                element: <CameraUpsert />,
            },
            {
                path: 'cameras/:id/edit',
                element: <CameraUpsert />,
            },
            {
                path: 'cameras/:id',
                element: <DetailsCamera />,
            },
            {
                path: 'cameras/:id/new-backdate',
                element: <NewBackdateScreen />,
            },
            {
                path: 'cameras/:id/new-potential-pcn',
                element: <NewBackdateScreen />,
            },
            {
                path: 'cameras/:id/backdate-runs/:runId',
                element: <BackdateDetailScreen />,
            },
            {
                path: 'cameras/:id/potential-pcn-runs/:runId',
                element: <BackdateDetailScreen />,
            },
            {
                path: 'cameras/:id/backdate-runs/:runId/review/:itemId',
                element: <BackdateReviewScreen />,
            },
            {
                path: 'cameras/:id/potential-pcn-runs/:runId/review/:itemId',
                element: <BackdateReviewScreen />,
            },
            {
                path: 'cameras/:id/live-photos',
                element: <LivePhotosPage />,
            },
            {
                path: 'cameras/:id/add/:type',
                element: <UpsertGeoZonesHandler />,
            },
            {
                path: 'cameras/:id/edit/:geoZoneId/:type',
                element: <UpsertGeoZonesHandler />,
            },
            // {
            //     path: 'cameras/:id/overview/:geoZoneId',
            //     element: <GeoZonesDetailCameraPTZ />,
            // },
            {
                path: 'cameras/:id/overview/:geoZoneId/add',
                element: <AddGeoZonesCameraPTZ />,
            },
            {
                path: 'zone-management',
                element: <ZoneManagement />,
            },
            {
                path: 'zone-management/:id',
                element: <ZoneDetails />,
            },
            {
                path: 'map-overview',
                element: <MapOverview />,
            },
            {
                path: 'zone-dashboard',
                element: <ZoneDashboard />,
            },
            {
                path: VerificationQueueMode.Verification,
                element: (
                    <VerificationProvider mode={VerificationQueueMode.Verification}>
                        <Outlet />
                    </VerificationProvider>
                ),
                children: [
                    { index: true, element: <ListPCByZone /> },
                    { path: ':zoneId', element: <VerificationsByZone /> },
                    { path: ':zoneId/:groupPcId', element: <WrapPCDetail readonly /> },
                    { path: 'contravention/:contraventionId', element: <WrapPCDetail /> },
                    { path: ':zoneId/step/:step', element: <WrapPCDetail /> },
                    { path: ':zoneId/:groupPcId/audit', element: <WrapPCDetail readonly /> },
                ],
            },
            {
                path: 'overview-verification',
                element: <VerificationsOverviewScreen />,
            },
            {
                path: 'camera-overview',
                element: <CameraOverviewPageContext />,
                children: [{ index: true, element: <CameraOverViewPage /> }],
            },
            {
                path: 'task-management',
                element: <TaskManagementScreen />,
            },
            // {
            //     path: 'cameraTime',
            //     element: <CameraTimeScreen />,
            // },
            {
                path: 'insights',
                element: <ReportBI />,
            },
            {
                path: 'pcns-chart',
                element: <InsightHeatMap />,
            },
            // {
            //     path: 'reports-analysing',
            //     element: <ReportsAnalyzing />,
            // },
            {
                path: 'overview-verification/:groupPcId',
                element: <WrapPCDetail readonly />,
            },
            {
                path: 'overview-verification/:groupPcId/audit',
                element: <WrapPCDetail readonly />,
            },
            {
                path: 'locations',
                element: <Locations />,
            },

            {
                path: 'manual-creation-pcn',
                element: <PCNCreationScreen />,
            },
            {
                path: 'exemption-list',
                element: <PermitsEcamVehicleScreen />,
            },
            {
                path: 'exemptions/create',
                element: <ExemptionCreateScreen />,
            },
            {
                path: 'configuration-template',
                element: <ConfigurationTemplateScreen />,
            },
            {
                path: 'check-photo',
                element: <CheckPhotoScreen />,
            },
            // {
            //     path: 'car-parking-list',
            //     element: <CarParkingListScreen />,
            // },
            // {
            //     path: 'car-parking-list/:id',
            //     element: <CarParkingDetails />,
            // },
            {
                path: 'alert',
                element: <CameraNotifications />,
            },
            {
                path: 'sim-management',
                element: <SIMManagement />,
            },
            // {
            //     path: 'ecam',
            //     element:
            // <FrameCrop /> ,
            // },

            {
                path: 'logout',
                element: <Logout />,
            },

            // eCam v2 — Tracks, Dashboard, Review (requires ECAM_V2_HTTP_ENABLED=true on backend)
            {
                path: 'vehicle-track',
                element: <EcamV2TracksPage />,
            },
            {
                path: 'funnel-dashboard',
                element: <EcamV2DashboardPage />,
            },
            {
                path: 'tracks-verification-dashboard',
                element: <EcamV2TracksDashboardPage />,
            },
            {
                path: 'pre-process-queue',
                element: <EcamV2ReviewZonesPage />,
            },
            {
                path: 'pre-process-queue/:zoneId',
                element: <EcamV2ReviewDetailPage />,
            },
            // {
            //     path: 'dashboard',
            //     element: <Dashboard /> ,
            // },

            // {
            //     path: 'crop',
            //     element: <CropIMG />,
            // },

            // {
            // 	path: "*",
            // 	action: () => redirect("/ecam"),
            // },

            {
                path: '/page-not-found',
                element: <PageNotFound />,
            },

            {
                path: '*',
                element: <Navigate to={'/page-not-found'} />,
            },
        ],
    },
]);

function App() {
    return <RouterProvider router={router} />;
}

export default App;
